import React from "react";
import { useTranslation } from "react-i18next";
import {
  WORKING_FILL,
  WORKING_STROKE,
  VARIANT_STROKE,
  LINE,
  LABEL,
  SURFACE,
  jitter,
  DiagramFigure,
  Legend,
  LegendItem,
} from "./diagramTheme";

const VIEW_W = 440;
const VIEW_H = 180;

const COLS = 8;
const ROWS_OF_FRAGMENTS = 6;
const TOTAL = COLS * ROWS_OF_FRAGMENTS;

// Spread across the sample rather than clustered, so the eye reads "a small
// share throughout" instead of "a clump". The exact count is not a clinical
// claim: the copy says the placental share is a minority and varies, and
// deliberately quotes no fetal-fraction percentage.
const PLACENTAL = new Set([5, 13, 22, 30, 39, 44]);

const BOX = { x: 18, y: 34, w: 404, h: 132, r: 14 };

const fragmentPosition = (index) => {
  const col = index % COLS;
  const row = Math.floor(index / COLS);
  const cellW = BOX.w / COLS;
  const cellH = BOX.h / ROWS_OF_FRAGMENTS;
  return {
    x: BOX.x + cellW * (col + 0.5) + jitter(index, 12.9898, cellW * 0.34),
    y: BOX.y + cellH * (row + 0.5) + jitter(index, 78.233, cellH * 0.36),
    angle: jitter(index, 39.425, 90),
  };
};

// Spotting the placental minority is the whole point of the picture, so the
// two fragment types cannot differ by hue alone. VARIANT_FILL against
// WORKING_FILL is only 1.5:1 in luminance — it survives greyscale and
// dichromacy in name only, and worst of all under deuteranopia. Placental
// fragments are drawn solid in the deeper amber (2.7:1 against the maternal
// fill, 3.1:1 against the page) and with a heavier stroke, so weight carries
// the distinction as well as colour. A dash or hatch was the other option and
// turns to mush at 14x5px.
const PLACENTAL_STROKE_W = 1.6;

const Fragment = ({ index }) => {
  const { x, y, angle } = fragmentPosition(index);
  const isPlacental = PLACENTAL.has(index);
  return (
    <rect
      x={-7}
      y={-2.5}
      width={14}
      height={5}
      rx={2.5}
      transform={`translate(${x} ${y}) rotate(${angle})`}
      fill={isPlacental ? VARIANT_STROKE : WORKING_FILL}
      stroke={isPlacental ? VARIANT_STROKE : WORKING_STROKE}
      strokeWidth={isPlacental ? PLACENTAL_STROKE_W : 1}
    />
  );
};

const FragmentSwatch = ({ placental }) => (
  <svg width="18" height="10" aria-hidden="true" focusable="false">
    <rect
      x={1}
      y={2}
      width={14}
      height={5}
      rx={2.5}
      fill={placental ? VARIANT_STROKE : WORKING_FILL}
      stroke={placental ? VARIANT_STROKE : WORKING_STROKE}
      strokeWidth={placental ? PLACENTAL_STROKE_W : 1}
    />
  </svg>
);

// Same split as InheritanceDiagram: the figure's own labels and notes are the
// diagram's wherever it is shown, while the wrapper copy, caption and
// screen-reader summary belong to the section showing it.
function CellFreeDnaDiagram({
  serviceKey = "prenatalScreening",
  shellBase,
  bare = false,
}) {
  const { t } = useTranslation();
  const base = `landing.service.${serviceKey}.diagram`;
  const notes = t(`${base}.notesArray`, { returnObjects: true });

  return (
    <DiagramFigure
      base={shellBase || base}
      t={t}
      bare={bare}
      legend={
        <Legend>
          <LegendItem swatch={<FragmentSwatch />}>
            {t(`${base}.legend.maternal`)}
          </LegendItem>
          <LegendItem swatch={<FragmentSwatch placental />}>
            {t(`${base}.legend.placental`)}
          </LegendItem>
        </Legend>
      }
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        width="100%"
        role="img"
        aria-labelledby="cfdna-title"
        style={{ maxWidth: "560px", height: "auto", display: "block" }}
      >
        {/* Named for what the image shows, not for the one label inside it.
            The full prose description lives in DiagramFigure's sr-only
            paragraph; repeating it in a <desc> here made screen readers
            announce the whole paragraph twice. */}
        <title id="cfdna-title">{t(`${base}.title`)}</title>

        <text x={BOX.x} y={20} fontSize={14} fontWeight="bold" fill={LABEL}>
          {t(`${base}.sampleLabel`)}
        </text>

        <rect
          x={BOX.x}
          y={BOX.y}
          width={BOX.w}
          height={BOX.h}
          rx={BOX.r}
          fill={SURFACE}
          stroke={LINE}
          strokeWidth={1.5}
        />

        {Array.from({ length: TOTAL }, (_, i) => (
          <Fragment key={i} index={i} />
        ))}
      </svg>

      {/* Kept out of the SVG: a sentence this long overflows the viewBox and
          gets clipped, and SVG text does not wrap. */}
      <p style={{ fontSize: "13px", color: LABEL, margin: "8px 0 0" }}>
        {t(`${base}.scaleNote`)}
      </p>

      <ul className="service-prose-list" style={{ marginTop: "18px" }}>
        {Array.isArray(notes) &&
          notes.map((note, i) => (
            <li key={i} style={{ color: "#4c5670" }}>
              {note}
            </li>
          ))}
      </ul>
    </DiagramFigure>
  );
}

export default CellFreeDnaDiagram;
